import { AxiosError } from "axios";
import { useMutation, useQuery } from "react-query";
import { toast } from "sonner";
import * as z from "zod";
import { useApi } from "./useApi";
import { useActiveGithubOrgForUser } from "./useConnectedGithubOrgs";

const workflowSchema = z.string();

const workflowsSchema = z.array(workflowSchema);

export type Workflow = z.infer<typeof workflowSchema>;

// Types for enhanced workflow file detection (runs-on-files endpoint)
export type WorkflowFileType = "workflow" | "reusable_workflow";

const workflowFileDataSchema = z.object({
  path: z.string(),
  name: z.string(),
  type: z.enum(["workflow", "reusable_workflow"]),
  content: z.string().nullable().optional(),
});

export type WorkflowFileData = z.infer<typeof workflowFileDataSchema>;

const runsOnFilesResponseSchema = z.object({
  workflows: z.array(workflowFileDataSchema),
  reusableWorkflows: z.array(workflowFileDataSchema),
});

export type RunsOnFilesResponse = z.infer<typeof runsOnFilesResponseSchema>;

const migrationPrResponseSchema = z.object({
  message: z.string(),
  pr_url: z.string(),
  codesmith_enabled: z.boolean().optional().default(false),
  managed_pr_id: z.string().nullable().optional(),
  session_id: z.string().nullable().optional(),
});

export type MigrationPrResponse = z.infer<typeof migrationPrResponseSchema>;

// Hook to fetch all files with runs-on directives (workflows and reusable workflows)
export function useRunsOnFilesForRepository(repositoryName: string) {
  const api = useApi(60000);
  const { activeGithubOrg } = useActiveGithubOrgForUser();

  const { data, error, isLoading } = useQuery(
    ["runs-on-files", activeGithubOrg, repositoryName],
    async () => {
      if (repositoryName === "") {
        return { workflows: [], reusableWorkflows: [] };
      }
      const response = await api.get(
        `user/github/orgs/${activeGithubOrg}/migrate-runners/repositories/${repositoryName}/runs-on-files`,
      );
      return runsOnFilesResponseSchema.parse(response.data);
    },
    {
      suspense: true,
      staleTime: Infinity,
      retry: 0,
      useErrorBoundary: false,
    },
  );

  return {
    workflows: data?.workflows ?? [],
    reusableWorkflows: data?.reusableWorkflows ?? [],
    error,
    isLoading,
  } as {
    workflows: WorkflowFileData[];
    reusableWorkflows: WorkflowFileData[];
    error: AxiosError | undefined;
    isLoading: boolean;
  };
}

// Job schema for runners endpoint - simple array format
const jobsSchema = z
  .record(z.string(), z.array(z.string()))
  .or(z.array(z.any())); // Handle empty array case

export type Jobs = z.infer<typeof jobsSchema>;

// Schema for Docker detection endpoint response
const dockerJobSchema = z.object({
  isAlreadyOnBlacksmith: z.boolean(),
  isPlannedMigration: z.boolean(),
  dockerBuild: z.object({
    detected: z.boolean(),
  }),
});

const dockerJobsSchema = z.record(z.string(), dockerJobSchema);
export type DockerJobs = z.infer<typeof dockerJobsSchema>;

export function useJobsForWorkflow({
  workflowPath,
  repositoryName,
}: {
  workflowPath: string; // Full path like .github/workflows/ci.yml or .github/actions/build/action.yml
  repositoryName: string;
}) {
  const api = useApi(60000);
  const { activeGithubOrg } = useActiveGithubOrgForUser();

  const { data, isLoading, error } = useQuery(
    ["jobs", activeGithubOrg, repositoryName, workflowPath],
    async () => {
      if (repositoryName === "" || workflowPath === "") {
        return {};
      }
      const response = await api.get(
        `user/github/orgs/${activeGithubOrg}/migrate-runners/repositories/${repositoryName}/workflows/${encodeURIComponent(
          workflowPath,
        )}/jobs`,
      );
      const jobs: Jobs = jobsSchema.parse(response.data);
      return jobs;
    },
    {
      suspense: false,
      // NB: Never consider this query stale since we have a `useEffect` that hydrates the runner paths
      // for when this query is first run.
      staleTime: Infinity,
      retry: 0,
      useErrorBoundary: false,
      onError: (error: AxiosError) => {
        toast.error("Could not fetch all jobs");
      },
    },
  );

  return {
    jobs: data || {},
    isLoading,
    error,
  };
}

// Hook to fetch Docker jobs for a workflow using POST request
export function useDockerJobsForWorkflow({
  workflowPath,
  repositoryName,
  plannedMigrations,
}: {
  workflowPath: string; // Full path like .github/workflows/ci.yml or .github/actions/build/action.yml
  repositoryName: string;
  plannedMigrations: string[]; // Job names that user plans to migrate
}) {
  const api = useApi(60000);
  const { activeGithubOrg } = useActiveGithubOrgForUser();

  const { data, isLoading, error } = useQuery(
    [
      "docker-jobs",
      activeGithubOrg,
      repositoryName,
      workflowPath,
      plannedMigrations,
    ],
    async () => {
      if (repositoryName === "" || workflowPath === "") {
        return {};
      }
      const response = await api.post(
        `user/github/orgs/${activeGithubOrg}/migrate-runners/repositories/${repositoryName}/workflows/${encodeURIComponent(
          workflowPath,
        )}/docker-jobs`,
        {
          plannedMigrations,
        },
      );
      const jobs: DockerJobs = dockerJobsSchema.parse(response.data);
      return jobs;
    },
    {
      suspense: false,
      staleTime: Infinity,
      useErrorBoundary: false,
    },
  );

  return {
    jobs: data || {},
    isLoading,
    error,
  };
}

export function useMigrateRunnersForRepository({
  repositoryName,
}: {
  repositoryName: string;
}) {
  const api = useApi(45000);
  const { activeGithubOrg } = useActiveGithubOrgForUser();

  const { mutate, isLoading, data, error } = useMutation(
    async ({
      updatedRunnerPaths,
      dockerCachingJobs,
    }: {
      updatedRunnerPaths: Record<string, Record<string, string[]>>;
      dockerCachingJobs?: Record<string, Record<string, boolean>>;
    }) => {
      // Send both runner migrations and Docker caching jobs to backend
      // docker_caching_jobs format: { "workflow.yml": { "build": true, "test": false } }
      const response = await api.post(
        `user/github/orgs/${activeGithubOrg}/migrate-runners/repositories/${repositoryName}/workflows`,
        {
          mapping: updatedRunnerPaths,
          docker_caching_jobs: dockerCachingJobs,
        },
      );

      return migrationPrResponseSchema.parse(response.data);
    },
    {
      onError: (error: AxiosError) => {
        if (error.response?.status === 403) {
          // Show an error toast if the user does not have permissions to access the repo.
          toast.error(
            "The Blacksmith app was not able to create a pull request; do you have restrictive rulesets on your repository?",
          );
          return;
        } else {
          toast.error(
            "An error occurred while creating the pull request, please try again",
          );
        }
      },
    },
  );

  return {
    doMigrateRunners: mutate,
    isLoading,
    migrationPr: data,
    prUrl: data?.pr_url,
    codesmithEnabled: data?.codesmith_enabled ?? false,
    managedPrId: data?.managed_pr_id ?? null,
    sessionId: data?.session_id ?? null,
    migrateError: error,
  };
}
